import { useEffect, useRef, useState } from 'react';
import {
  DocumentDetail,
  fileLabel,
  filePeriod,
  filePeriodLabel,
  periodLabel,
  timestamp,
} from './detailModel';
export default function DecisionDialog({
  action,
  doc,
  person,
  scope,
  busy,
  error,
  reason,
  onReason,
  onClose,
  onConfirm,
  aiSummary,
}: {
  aiSummary?: string;
  action: 'approve' | 'reject' | 'revision';
  doc: DocumentDetail;
  person: string;
  scope: string | null;
  busy: boolean;
  error: string | null;
  reason: string;
  onReason: (value: string) => void;
  onClose: () => void;
  onConfirm: (message?: string) => void;
}) {
  const [includeAi, setIncludeAi] = useState(false);
  const sharedSummary =
    action === 'reject' && includeAi ? aiSummary?.trim() : '';
  const finalReason = sharedSummary
    ? `Resumen de la revisión asistida por IA:\n${sharedSummary}${reason.trim() ? `\n\nComentario del revisor:\n${reason.trim()}` : ''}`
    : reason.trim();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const element = dialog.current!;
    element.showModal();
    return () => {
      element.close();
      previous?.focus();
    };
  }, []);
  const label =
    action === 'approve'
      ? 'Confirmar aprobación'
      : action === 'reject'
        ? 'Confirmar rechazo'
        : 'Solicitar revisión';
  return (
    <dialog
      ref={dialog}
      aria-labelledby="decision-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      className="m-auto w-[calc(100%-2rem)] max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-0 text-slate-900 shadow-xl backdrop:bg-black/40"
    >
      <div className="p-6">
        <h2 id="decision-title" className="text-xl font-semibold">
          {label}
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          La decisión se aplicará a esta versión del documento.
        </p>
        <dl className="my-5 space-y-2 rounded-lg bg-slate-50 p-4 text-sm">
          <div>
            <dt className="text-slate-500">Persona</dt>
            <dd className="break-words font-medium">{person}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Documento y archivo</dt>
            <dd className="break-words font-medium">
              {doc.documentName || doc.requirement?.definition?.name} —{' '}
              {fileLabel(doc)}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Periodo del archivo</dt>
            <dd>{filePeriodLabel(doc)}</dd>
          </div>
          {scope !== filePeriod(doc) && (
            <div>
              <dt className="text-slate-500">Periodo consultado</dt>
              <dd>{periodLabel(scope)}</dd>
            </div>
          )}
          <div>
            <dt className="text-slate-500">Versión cargada</dt>
            <dd>{timestamp(doc.upload_date)}</dd>
          </div>
        </dl>
        {action === 'reject' && aiSummary && (
          <label className="mb-4 flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm">
            <input
              type="checkbox"
              checked={includeAi}
              disabled={busy}
              onChange={(e) => setIncludeAi(e.target.checked)}
              className="mt-1"
            />
            <span>
              Incluir resumen de IA en el motivo que recibirá el usuario
            </span>
          </label>
        )}
        {action !== 'approve' && (
          <label className="block text-sm font-medium">
            {action === 'reject'
              ? sharedSummary
                ? 'Comentario del revisor (opcional)'
                : 'Motivo del rechazo'
              : 'Cambios solicitados'}
            <textarea
              autoFocus
              value={reason}
              disabled={busy}
              onChange={(event) => onReason(event.target.value)}
              rows={4}
              className="mt-2 w-full rounded-lg border border-slate-300 p-3 focus:ring-2 focus:ring-blue-600"
            />
          </label>
        )}
        {sharedSummary && (
          <section
            aria-label="Mensaje para el usuario"
            className="mt-4 rounded-lg bg-slate-50 p-4"
          >
            <h3 className="text-sm font-semibold">Mensaje para el usuario</h3>
            <p className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
              {finalReason}
            </p>
          </section>
        )}
        <p className="mt-3 text-xs text-slate-500">
          Se intentará enviar una notificación al usuario después de guardar.
        </p>
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-800">
            {error}
          </p>
        )}
        {busy && (
          <p role="status" className="mt-3 text-sm">
            Guardando decisión…
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-between gap-4">
          <button
            autoFocus={action === 'approve'}
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            disabled={busy || (action !== 'approve' && !finalReason)}
            onClick={() => onConfirm(finalReason)}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${action === 'reject' ? 'bg-red-700' : action === 'revision' ? 'bg-amber-800' : 'bg-blue-700'}`}
          >
            {label}
          </button>
        </div>
      </div>
    </dialog>
  );
}
